<template>
  <v-dialog :value="value" max-width="620" :fullscreen="isMobile" scrollable @input="$emit('input', $event)">
    <div class="sm">
      <header class="sm__head">
        <span class="sm__icon"><v-icon color="primary">mdi-tune-variant</v-icon></span>
        <div class="sm__titles">
          <div class="sm__title">{{ $t('crmManageStatuses') }}</div>
          <div class="sm__sub">{{ $t('crmManageStatusesHint') }}</div>
        </div>
        <button type="button" class="sm__close" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></button>
      </header>

      <div class="sm__tabs">
        <button v-for="e in entities" :key="e" type="button" :class="{ on: entity === e }" @click="entity = e">{{ $t('crmEntity_' + e) }}</button>
      </div>

      <div class="sm__body">
        <v-alert v-if="error" type="error" text dense>{{ error }}</v-alert>
        <draggable v-model="list" handle=".sm__grip" :animation="160" ghost-class="sm__ghost" @end="saveOrder">
          <div v-for="s in list" :key="s.id" class="sm__row" :class="{ 'sm__row--edit': editing === s.id }">
            <span class="sm__grip"><v-icon small>mdi-drag-vertical</v-icon></span>
            <template v-if="editing === s.id">
              <div class="sm__edit">
                <div class="sm__colors">
                  <button v-for="c in palette" :key="c" type="button" class="sm__color" :class="{ on: draft.color === c }" :style="{ background: c }" @click="draft.color = c" />
                </div>
                <v-text-field v-model="draft.title" :label="$t('crmStatusTitle')" outlined dense hide-details autofocus @keydown.enter="saveEdit(s)" />
                <v-select v-model="draft.category" :items="categoryItems" :label="$t('crmStatusCategory')" outlined dense hide-details :disabled="s.is_system" />
                <v-switch v-model="draft.is_default" inset dense hide-details :label="$t('crmStatusDefault')" />
                <div class="sm__edit-foot">
                  <v-btn small depressed color="primary" :loading="busy" @click="saveEdit(s)">{{ $t('crmSave') }}</v-btn>
                  <v-btn small text @click="editing = null">{{ $t('crmCancel') }}</v-btn>
                </div>
              </div>
            </template>
            <template v-else>
              <span class="sm__dot" :style="{ background: s.color || '#8a94a6' }" />
              <div class="sm__main">
                <b>{{ s.title || $t('crmStatus_' + s.key) }}</b>
                <small>
                  {{ $t('crmCategory_' + s.category) }}
                  <span v-if="s.is_default" class="sm__tag">{{ $t('crmStatusDefault') }}</span>
                  <span v-if="s.is_system" class="sm__tag sm__tag--sys"><v-icon x-small>mdi-lock-outline</v-icon>{{ $t('crmStatusSystem') }}</span>
                </small>
              </div>
              <button type="button" class="sm__btn" :title="$t('crmEdit')" @click="startEdit(s)"><v-icon small>mdi-pencil-outline</v-icon></button>
              <button v-if="!s.is_system" type="button" class="sm__btn sm__btn--del" :title="$t('crmDelete')" @click="remove(s)"><v-icon small>mdi-delete-outline</v-icon></button>
            </template>
          </div>
        </draggable>

        <div class="sm__add">
          <div class="sm__colors">
            <button v-for="c in palette" :key="c" type="button" class="sm__color" :class="{ on: add.color === c }" :style="{ background: c }" @click="add.color = c" />
          </div>
          <div class="sm__add-row">
            <v-text-field v-model="add.title" :label="$t('crmNewStatus')" outlined dense hide-details @keydown.enter="create" />
            <v-select v-model="add.category" :items="categoryItems" :label="$t('crmStatusCategory')" outlined dense hide-details class="sm__cat" />
            <v-btn depressed color="primary" :disabled="!add.title" :loading="busy" class="sm__add-btn" @click="create"><v-icon small>mdi-plus</v-icon></v-btn>
          </div>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import draggable from 'vuedraggable'
import api, { crmError } from '../crmApi'
import { crm, reloadStatuses } from '../crmStore'

const PALETTE = ['#2166d1', '#00838f', '#0f9d76', '#6a4fd1', '#8b5cf6', '#d9822b', '#c0392b', '#8a94a6']

export default {
  name: 'CrmStatusManager',
  components: { draggable },
  inject: ['shell'],
  props: { value: Boolean, initialEntity: { type: String, default: 'lead' } },
  data: () => ({ entity: 'lead', list: [], editing: null, draft: {}, add: { title: '', color: PALETTE[0], category: 'open' }, busy: false, error: '' }),
  computed: {
    entities () { return ['lead', 'person', 'company'] },
    palette () { return PALETTE },
    categoryItems () { return ['open', 'won', 'lost', 'archived'].map(c => ({ value: c, text: this.$t('crmCategory_' + c) })) }
  },
  watch: {
    value (v) { if (v) { this.entity = this.initialEntity; this.sync() } },
    entity () { this.sync() }
  },
  methods: {
    sync () {
      this.list = (crm.statuses[this.entity] || []).slice()
      this.editing = null
      this.error = ''
    },
    async refresh () {
      await reloadStatuses()
      this.sync()
      this.shell.$emit('changed', { type: this.entity, record: null })
    },
    startEdit (s) {
      this.editing = s.id
      this.draft = { title: s.title, color: s.color || PALETTE[0], category: s.category, is_default: !!s.is_default }
    },
    async run (fn) {
      this.busy = true
      this.error = ''
      try { await fn(); await this.refresh() } catch (e) { this.error = crmError(e).message } finally { this.busy = false }
    },
    saveEdit (s) {
      const body = { ...this.draft }
      if (s.is_system) delete body.category
      return this.run(() => api.updateStatus(s.id, body))
    },
    create () {
      if (!this.add.title) return
      return this.run(async () => {
        await api.createStatus({ entity_type: this.entity, ...this.add, sort_order: this.list.length })
        this.add = { title: '', color: this.add.color, category: 'open' }
      })
    },
    saveOrder () { return this.run(() => api.reorderStatuses(this.entity, this.list.map(s => s.id))) },
    async remove (s) {
      const others = this.list.filter(x => x.id !== s.id)
      const target = others.find(x => x.is_default) || others[0]
      const ok = await this.shell.ask(this.$t('crmDeleteStatusConfirm', { name: s.title, to: target ? target.title : '—' }), this.$t('crmTypeToConfirm'), '')
      if (ok === null || String(ok).trim() !== String(s.title).trim()) {
        if (ok) this.shell.toast(this.$t('crmConfirmMismatch'), { error: true })
        return
      }
      return this.run(() => api.deleteStatus(s.id, target && target.key))
    }
  }
}
</script>

<style scoped>
.sm { background: #fff; border-radius: 6px; overflow: hidden; display: flex; flex-direction: column; max-height: 90vh; }
.theme--dark .sm { background: #1e1e1e; }
.sm__head { display: flex; align-items: center; gap: 12px; padding: 14px 18px; border-bottom: 1px solid rgba(15, 23, 42, .07); flex-shrink: 0; }
.sm__icon { width: 40px; height: 40px; border-radius: 6px; background: rgba(25, 118, 210, .1); display: flex; align-items: center; justify-content: center; }
.sm__titles { flex: 1; min-width: 0; }
.sm__title { font-weight: 700; }
.sm__sub { font-size: .76rem; opacity: .6; }
.sm__close { width: 36px; height: 36px; border-radius: 6px; background: rgba(0, 0, 0, .05); display: flex; align-items: center; justify-content: center; }
.sm__tabs { display: flex; gap: 18px; padding: 0 18px; border-bottom: 1px solid rgba(15, 23, 42, .07); flex-shrink: 0; }
.sm__tabs button { padding: 10px 0; font-size: .85rem; font-weight: 600; opacity: .6; border-bottom: 2px solid transparent; }
.sm__tabs button.on { opacity: 1; color: var(--v-primary-base); border-bottom-color: var(--v-primary-base); }
.sm__body { padding: 16px 18px 20px; overflow-y: auto; flex: 1; }
.sm__row { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border: 1px solid rgba(15, 23, 42, .08); border-radius: 6px; margin-bottom: 6px; background: #fff; }
.theme--dark .sm__row { background: #262626; }
.sm__row--edit { align-items: flex-start; border-color: var(--v-primary-base); }
.sm__ghost { opacity: .4; }
.sm__grip { cursor: grab; opacity: .45; display: inline-flex; }
.sm__dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
.sm__main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.sm__main b { font-size: .86rem; }
.sm__main small { font-size: .72rem; opacity: .7; display: flex; align-items: center; gap: 6px; }
.sm__tag { padding: 0 6px; border-radius: 6px; background: rgba(25, 118, 210, .1); color: var(--v-primary-base); font-weight: 600; }
.sm__tag--sys { background: rgba(127, 127, 127, .14); color: inherit; display: inline-flex; align-items: center; gap: 2px; }
.sm__btn { width: 30px; height: 30px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; }
.sm__btn:hover { background: rgba(127, 127, 127, .12); }
.sm__btn--del:hover ::v-deep .v-icon { color: #c0392b !important; }
.sm__edit { flex: 1; display: flex; flex-direction: column; gap: 14px; padding: 4px 0; }
.sm__edit-foot { display: flex; gap: 8px; }
.sm__colors { display: flex; gap: 6px; flex-wrap: wrap; }
.sm__color { width: 22px; height: 22px; border-radius: 50%; border: 2px solid transparent; box-shadow: 0 0 0 1px rgba(15, 23, 42, .1); }
.sm__color.on { border-color: #fff; box-shadow: 0 0 0 2px var(--v-primary-base); }
.sm__add { margin-top: 16px; padding: 12px; border-radius: 6px; border: 1px dashed rgba(15, 23, 42, .18); display: flex; flex-direction: column; gap: 12px; }
.sm__add-row { display: flex; gap: 8px; align-items: center; }
.sm__cat { max-width: 170px; }
.sm__add-btn { min-width: 40px !important; height: 40px !important; border-radius: 6px !important; }
.sm ::v-deep .v-input { height: auto !important; margin: 0; padding: 0; }
.sm ::v-deep fieldset { border-radius: 6px; }
@media (max-width: 799px) {
  .sm { max-height: none; height: 100%; border-radius: 0; }
  .sm__add-row { flex-wrap: wrap; }
  .sm__cat { max-width: none; flex: 1; }
}
</style>
